import React, { useContext } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import Form from '@atlaskit/form';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';

import { ExperienceTrackerContext, AI_AUDIO_BRIEFING } from '@confluence/experience-tracker';

import { useAudioBriefing } from '../../../../hooks/useAudioBriefing';
import type { CustomLengthType, CustomToneType } from '../../../../types/customizationTypes';

import { ToneSelector } from './ToneSelector';
import { DurationSelector } from './DurationSelector';

const styles = cssMap({
	grow: {
		flex: 1,
	},
});

const i18n = defineMessages({
	applyButtonLabel: {
		id: 'audio.audio-briefing-popup.customize-tab.apply',
		defaultMessage: 'Apply customizations',
		description: 'Label for the button used to apply briefing customizations',
	},
});

interface CustomizationFormValues {
	length: CustomLengthType;
	tone: {
		value: CustomToneType;
	};
}

export const AudioBriefingPopupCustomizePanel = ({
	audioRef,
}: {
	audioRef: React.RefObject<HTMLAudioElement>;
}) => {
	const { formatMessage } = useIntl();

	const [{ activeContent }, { fetchBriefing, setCustomization }] = useAudioBriefing();

	const experienceTracker = useContext(ExperienceTrackerContext);

	const handleStartNewBriefing = (data: CustomizationFormValues) => {
		if (audioRef?.current) {
			audioRef.current.pause();
		}
		setCustomization({
			length: data.length,
			tone: data.tone.value,
		});

		experienceTracker.start({
			name: AI_AUDIO_BRIEFING,
			attributes: {
				contentId: activeContent?.id,
			},
		});
		fetchBriefing();
	};

	return (
		<Box xcss={styles.grow}>
			<Form<CustomizationFormValues> onSubmit={handleStartNewBriefing}>
				{({ formProps }) => (
					<form {...formProps}>
						<Stack space="space.100">
							<ToneSelector />
							<DurationSelector />
							<Box paddingBlockStart="space.150">
								<Button type="submit" appearance="primary" shouldFitContainer>
									{formatMessage(i18n.applyButtonLabel)}
								</Button>
							</Box>
						</Stack>
					</form>
				)}
			</Form>
		</Box>
	);
};
